// PhotoHealthCard — scan every room/gallery image, list the ones that no longer load, remove them in one click.
import React, { useEffect, useState } from 'react';
import { ImageOff, Loader2, ScanSearch, Trash2, CheckCircle2 } from 'lucide-react';
import { apiCall } from './_shared';

const PhotoHealthCard = ({ canEdit }) => {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = () => apiCall('/api/admin/photos/health').then(setH).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (h?.status !== 'running') return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [h]);

  const scan = async () => {
    setBusy('scan'); setMsg('');
    try { await apiCall('/api/admin/photos/health/scan', { method: 'POST' }); setH({ ...(h || {}), status: 'running', checked: 0, total: h?.total || '…' }); }
    catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${h.broken.length} broken image(s) from the rooms and the gallery? Guests will stop seeing grey boxes; you can upload replacements afterwards.`)) return;
    setBusy('remove'); setMsg('');
    try {
      const r = await apiCall('/api/admin/photos/health/remove-broken', { method: 'POST' });
      setMsg(`${r.removed} image(s) removed.${r.rooms_without_photos?.length ? ` Rooms now without photos: ${r.rooms_without_photos.join(', ')} — please upload new ones.` : ''}`);
      load();
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };

  const broken = h?.broken || [];
  const rows = broken.slice(0, 40).map((b) => (
    <li key={b.url} className="flex items-start gap-2 text-xs" data-testid="photo-health-row">
      <ImageOff className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
      <span className="min-w-0"><span className="font-mono break-all text-slate-700">{b.url.length > 90 ? `${b.url.slice(0, 87)}…` : b.url}</span>
        <span className="block text-slate-500">{b.reason}{b.room_names?.filter(Boolean).length ? ` · ${b.room_names.filter(Boolean).join(', ')}` : ''}</span></span>
    </li>
  ));
  const running = h?.status === 'running';
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${broken.length ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`} data-testid="photo-health-card">
      <div className="flex flex-wrap items-center gap-3">
        {broken.length ? <ImageOff className="w-4 h-4 text-red-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
        <span data-testid="photo-health-summary">
          {running ? `Checking photos… ${h.checked}/${h.total}` : h?.status === 'finished'
            ? (broken.length ? <><b>{broken.length}</b> of {h.total} images do not load</> : <>All {h.total} room &amp; gallery images load · checked {String(h.finished_at || '').slice(0, 10)}</>)
            : 'Photo check not run yet'}
        </span>
        <span className="flex-1" />
        {canEdit && (
          <button type="button" onClick={scan} disabled={running || busy === 'scan'} className="inline-flex items-center gap-1 font-semibold text-slate-800 border rounded-lg px-3 py-1.5 bg-white hover:bg-slate-100 disabled:opacity-60" data-testid="photo-health-scan">
            {running || busy === 'scan' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />} Scan photos
          </button>
        )}
        {canEdit && broken.length > 0 && !running && (
          <button type="button" onClick={remove} disabled={busy === 'remove'} className="inline-flex items-center gap-1 font-semibold text-white bg-red-600 rounded-lg px-3 py-1.5 hover:bg-red-700 disabled:opacity-60" data-testid="photo-health-remove">
            {busy === 'remove' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove broken ({broken.length})
          </button>
        )}
        {msg && <span className="text-emerald-700 text-xs basis-full" data-testid="photo-health-msg">{msg}</span>}
      </div>
      {rows.length > 0 && <ul className="mt-3 space-y-1.5 max-h-56 overflow-auto" data-testid="photo-health-list">{rows}{broken.length > 40 && <li className="text-xs text-slate-500">… and {broken.length - 40} more</li>}</ul>}
    </div>
  );
};

export default PhotoHealthCard;
